/**
 * On-screen touch controls: the mobile/tablet stand-in for mouse-look and WASD.
 *
 * The scheme is fixed by the art spec and intentionally not mirrored:
 *   - LEFT half of the screen is LOOK — a drag anywhere there accumulates a
 *     yaw/pitch delta, consumed each frame exactly like the mouse path.
 *   - RIGHT half is MOVE — a *floating* virtual joystick whose centre is wherever
 *     the thumb first lands; the drag vector (clamped to a small radius) becomes the
 *     move axis, and pushing near the edge counts as a sprint.
 *   - A fixed JUMP button sits above the move zone with its own listeners.
 *
 * Each zone carries a persistent, semi-transparent guide ring so the player can see
 * *where* to put their thumbs; the ring for a zone hides while that zone is active
 * (its live control — the look dot or the floating stick — takes over) and returns
 * on release.
 *
 * Multi-touch is honoured by remembering which zone each touch identifier was born
 * in: look and move work simultaneously because a touch belongs to the zone it
 * *started* in for its whole life, regardless of where it drags.
 *
 * All touch coordinates are mapped through viewport.mapClientToStage first, so the
 * controls behave correctly even when the whole app is CSS-rotated for forced
 * landscape (see src/utils/viewport.ts). The overlay lives inside #stage so it
 * rotates with everything else.
 */
import { getStage, mapClientToStage, stageWidth } from '@/utils/viewport';

/** Touch look is scaled up relative to mouse pixels so the drag "feel" matches the
 *  mouse sensitivity (radians/px) the player controller applies downstream. Tuneable. */
export const LOOK_TOUCH_SCALE = 2.4;

/** Radius (px) of the floating joystick: the drag distance mapped to a full-magnitude
 *  move axis. */
const STICK_RADIUS = 64;
/** Fraction of the radius beyond which the stick reads as a sprint. Deliberately
 *  near the very edge so ordinary walking never trips it by accident. */
const SPRINT_THRESHOLD = 0.95;

/** Overlay geometry (px). */
const STICK_BASE_SIZE = 128;
const STICK_THUMB_SIZE = 30;
const GUIDE_SIZE = 120;
const JUMP_BUTTON_SIZE = 76;

const RING = '1.5px solid rgba(43,43,43,0.28)'; // accent ink
const THUMB_FILL = 'rgba(43,43,43,0.35)';

export class TouchControls {
  private container: HTMLDivElement;
  private lookGuide: HTMLDivElement;
  private moveGuide: HTMLDivElement;
  private lookDot: HTMLDivElement;
  private stickBase: HTMLDivElement;
  private stickThumb: HTMLDivElement;
  private jumpButton: HTMLDivElement;

  private enabled = false;

  // Which touch identifier owns each zone (null = free). A touch belongs to the zone
  // it started in for its whole lifetime.
  private lookId: number | null = null;
  private stickId: number | null = null;

  // Look accumulation (already scaled), consumed each frame. Last position is in
  // stage space.
  private lookDX = 0;
  private lookDY = 0;
  private lookLastX = 0;
  private lookLastY = 0;

  // Floating-stick state (stage space).
  private stickCX = 0;
  private stickCY = 0;
  private axisX = 0;
  private axisZ = 0;
  private sprint = false;

  private jumpQueued = false;

  constructor() {
    const stage = getStage();

    // Overlay container — above the canvas, below the veil/narration UI root. It
    // fills #stage, which viewport.ts has already sized to the usable area below the
    // status bar, so mapped stage coordinates place elements exactly under the thumb.
    const container = document.createElement('div');
    container.style.cssText =
      'position:absolute;inset:0;z-index:5;pointer-events:none;opacity:0;' +
      'transition:opacity 0.3s ease;touch-action:none;' +
      'font-family:Inter,Helvetica Neue,system-ui,sans-serif;';
    this.container = container;

    // --- Persistent guide rings: "put your thumb here" for each zone. ---
    this.lookGuide = this.makeGuide('LOOK', '‹ ›', 'left');
    this.moveGuide = this.makeGuide('MOVE', '＋', 'right');

    // Look dot — rides the finger while looking, so the free-drag zone visibly reacts.
    const dot = document.createElement('div');
    dot.style.cssText =
      'position:absolute;left:0;top:0;width:30px;height:30px;border-radius:50%;' +
      `border:${RING};opacity:0;transition:opacity 0.2s ease;` +
      'pointer-events:none;will-change:transform;';
    this.lookDot = dot;

    // Floating joystick base + thumb (right zone), shown while moving.
    const base = document.createElement('div');
    base.style.cssText =
      `position:absolute;left:0;top:0;width:${STICK_BASE_SIZE}px;height:${STICK_BASE_SIZE}px;` +
      `border:${RING};border-radius:50%;opacity:0;transition:opacity 0.2s ease;` +
      'pointer-events:none;will-change:transform;';
    this.stickBase = base;

    const thumb = document.createElement('div');
    thumb.style.cssText =
      `position:absolute;left:0;top:0;width:${STICK_THUMB_SIZE}px;height:${STICK_THUMB_SIZE}px;` +
      `background:${THUMB_FILL};border-radius:50%;opacity:0;transition:opacity 0.2s ease;` +
      'pointer-events:none;will-change:transform;';
    this.stickThumb = thumb;

    // Jump button — on the LEFT, above the LOOK guide, with its own listeners. The
    // right thumb is busy holding the move stick, so putting jump on the same side
    // meant lifting off the stick to jump: you could never walk and jump together,
    // which is exactly what climbing onto a ledge needs. The left thumb can tap it
    // while the right keeps moving.
    const jump = document.createElement('div');
    // Offsets are plain px: the safe-area inset is already applied once, to #stage
    // in index.html, so the whole overlay sits inside the safe box.
    jump.style.cssText =
      `position:absolute;width:${JUMP_BUTTON_SIZE}px;height:${JUMP_BUTTON_SIZE}px;` +
      'left:56px;bottom:172px;' +
      `border:${RING};border-radius:50%;opacity:0;transition:opacity 0.3s ease;` +
      'display:flex;align-items:center;justify-content:center;' +
      'font-size:1.3rem;font-weight:300;color:rgba(43,43,43,0.35);' +
      'pointer-events:auto;touch-action:none;-webkit-user-select:none;user-select:none;';
    jump.textContent = '↑';
    this.jumpButton = jump;

    container.appendChild(this.lookGuide);
    container.appendChild(this.moveGuide);
    container.appendChild(dot);
    container.appendChild(base);
    container.appendChild(thumb);
    container.appendChild(jump);
    stage.appendChild(container);
  }

  /** Build a persistent, semi-transparent zone guide ring anchored to one side. */
  private makeGuide(label: string, glyph: string, side: 'left' | 'right'): HTMLDivElement {
    const g = document.createElement('div');
    // Plain px — #stage already carries the safe-area inset (see index.html).
    const anchor = side === 'left' ? 'left:34px;' : 'right:34px;';
    g.style.cssText =
      `position:absolute;${anchor}bottom:34px;` +
      `width:${GUIDE_SIZE}px;height:${GUIDE_SIZE}px;border:${RING};border-radius:50%;` +
      'background:rgba(43,43,43,0.05);opacity:0;transition:opacity 0.35s ease;' +
      'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.3em;' +
      'color:rgba(43,43,43,0.38);font-weight:300;pointer-events:none;';
    g.innerHTML =
      `<div style="font-size:1.3rem;letter-spacing:0.12em;line-height:1;">${glyph}</div>` +
      `<div style="font-size:0.58rem;letter-spacing:0.34em;padding-left:0.34em;">${label}</div>`;
    return g;
  }

  /** Reveal the overlay and start listening. Called by Input on first tap / enter. */
  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    this.container.style.opacity = '1';
    this.jumpButton.style.opacity = '1';
    this.refreshGuides();

    window.addEventListener('touchstart', this.onTouchStart, { passive: false });
    window.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('touchend', this.onTouchEnd, { passive: false });
    window.addEventListener('touchcancel', this.onTouchEnd, { passive: false });

    this.jumpButton.addEventListener('touchstart', this.onJumpStart, { passive: false });
    this.jumpButton.addEventListener('touchend', this.onJumpEnd, { passive: false });
  }

  /** Hide the overlay and stop listening; zeroes all transient state. */
  disable(): void {
    if (!this.enabled) return;
    this.enabled = false;
    this.container.style.opacity = '0';
    this.jumpButton.style.opacity = '0';
    this.lookGuide.style.opacity = '0';
    this.moveGuide.style.opacity = '0';
    this.lookDot.style.opacity = '0';
    this.hideStick();
    this.resetState();

    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);
    window.removeEventListener('touchcancel', this.onTouchEnd);
    this.jumpButton.removeEventListener('touchstart', this.onJumpStart);
    this.jumpButton.removeEventListener('touchend', this.onJumpEnd);
  }

  /** Guide rings show only while their zone is idle — the live control replaces them. */
  private refreshGuides(): void {
    if (!this.enabled) return;
    this.lookGuide.style.opacity = this.lookId === null ? '0.6' : '0';
    this.moveGuide.style.opacity = this.stickId === null ? '0.6' : '0';
  }

  /* -------- zone routing -------- */

  private onTouchStart = (e: TouchEvent): void => {
    const half = stageWidth() / 2; // re-read per touchstart so rotation is handled
    for (const t of Array.from(e.changedTouches)) {
      const p = mapClientToStage(t.clientX, t.clientY);
      if (p.x < half) {
        // LEFT — claim the look zone if it is free.
        if (this.lookId === null) {
          this.lookId = t.identifier;
          this.lookLastX = p.x;
          this.lookLastY = p.y;
          this.placeCentered(this.lookDot, p.x, p.y);
          this.lookDot.style.opacity = '1';
          this.refreshGuides();
        }
      } else {
        // RIGHT — anchor the floating stick if it is free.
        if (this.stickId === null) {
          this.stickId = t.identifier;
          this.stickCX = p.x;
          this.stickCY = p.y;
          this.axisX = 0;
          this.axisZ = 0;
          this.sprint = false;
          this.showStick(p.x, p.y);
          this.refreshGuides();
        }
      }
    }
    e.preventDefault();
  };

  private onTouchMove = (e: TouchEvent): void => {
    for (const t of Array.from(e.changedTouches)) {
      const p = mapClientToStage(t.clientX, t.clientY);
      if (t.identifier === this.lookId) {
        this.lookDX += (p.x - this.lookLastX) * LOOK_TOUCH_SCALE;
        this.lookDY += (p.y - this.lookLastY) * LOOK_TOUCH_SCALE;
        this.lookLastX = p.x;
        this.lookLastY = p.y;
        this.placeCentered(this.lookDot, p.x, p.y);
      } else if (t.identifier === this.stickId) {
        this.updateStick(p.x, p.y);
      }
    }
    e.preventDefault();
  };

  private onTouchEnd = (e: TouchEvent): void => {
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier === this.lookId) {
        this.lookId = null;
        this.lookDot.style.opacity = '0';
        this.refreshGuides();
      } else if (t.identifier === this.stickId) {
        this.stickId = null;
        this.axisX = 0;
        this.axisZ = 0;
        this.sprint = false;
        this.hideStick();
        this.refreshGuides();
      }
    }
    e.preventDefault();
  };

  /* -------- floating stick maths (stage space) -------- */

  private updateStick(x: number, y: number): void {
    const dx = x - this.stickCX;
    const dy = y - this.stickCY;
    const dist = Math.hypot(dx, dy);
    const clamped = Math.min(dist, STICK_RADIUS);
    let nx = 0;
    let ny = 0;
    if (dist > 0) {
      nx = dx / dist;
      ny = dy / dist;
    }
    const mag = clamped / STICK_RADIUS; // 0..1

    // Axis convention matches Input.moveAxis(): x = right(+), z = forward(-).
    // Pushing the stick UP means dy < 0, which yields z < 0 → forward. Correct.
    this.axisX = nx * mag;
    this.axisZ = ny * mag;
    this.sprint = mag > SPRINT_THRESHOLD;

    this.moveThumb(this.stickCX + nx * clamped, this.stickCY + ny * clamped);
  }

  private showStick(x: number, y: number): void {
    this.placeCentered(this.stickBase, x, y);
    this.moveThumb(x, y);
    this.stickBase.style.opacity = '1';
    this.stickThumb.style.opacity = '1';
  }

  private hideStick(): void {
    this.stickBase.style.opacity = '0';
    this.stickThumb.style.opacity = '0';
  }

  private moveThumb(x: number, y: number): void {
    this.placeCentered(this.stickThumb, x, y);
  }

  private placeCentered(el: HTMLDivElement, x: number, y: number): void {
    el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
  }

  /* -------- jump button (its own touches) -------- */

  private onJumpStart = (e: TouchEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    this.jumpQueued = true;
    this.jumpButton.style.opacity = '0.7';
  };

  private onJumpEnd = (e: TouchEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    this.jumpButton.style.opacity = '1';
  };

  /* -------- query API (merged by Input) -------- */

  /** The stick's move intent, or null when no right-zone touch is active. */
  moveAxis(): { x: number; z: number } | null {
    if (this.stickId === null) return null;
    return { x: this.axisX, z: this.axisZ };
  }

  /** Consume the accumulated (pre-scaled) touch look for this frame. */
  consumeLook(): { dx: number; dy: number } {
    const d = { dx: this.lookDX, dy: this.lookDY };
    this.lookDX = 0;
    this.lookDY = 0;
    return d;
  }

  /** Consume a queued jump (edge-triggered). */
  consumeJump(): boolean {
    if (this.jumpQueued) {
      this.jumpQueued = false;
      return true;
    }
    return false;
  }

  get sprinting(): boolean {
    return this.sprint;
  }

  private resetState(): void {
    this.lookId = null;
    this.stickId = null;
    this.lookDX = 0;
    this.lookDY = 0;
    this.axisX = 0;
    this.axisZ = 0;
    this.sprint = false;
    this.jumpQueued = false;
  }

  dispose(): void {
    this.disable();
    this.container.remove();
  }
}
